import React, { useState, useRef, useEffect } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/styles'
import {
  AppBar,
  Badge,
  IconButton,
  Toolbar,
  Hidden,
  colors,
  Typography,
  Avatar
} from '@material-ui/core'
import NotificationsIcon from '@material-ui/icons/NotificationsOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MenuIcon from '@material-ui/icons/Menu'
import { NotificationsPopover } from 'components'
import ProfilePopover from 'components/ProfilePopover'
import formatAMPM from 'utils/dateFormat'
import MenuCollapseIcon from 'assets/Icons/menu-icon.svg'
import { toggleCollapseMenu } from 'redux/actions'
import checkToken from 'utils/checkToken'
import appApiUrl from 'utils/apiUrl'
import { useSnackbar } from 'notistack'

const useStyles = makeStyles(theme => ({
  root: {
    boxShadow: 'none',
    backgroundColor: '#47494E'
  },
  flexGrow: {
    flexGrow: 1
  },
  search: {
    backgroundColor: 'rgba(255,255,255, 0.1)',
    borderRadius: 4,
    flexBasis: 300,
    height: 36,
    padding: theme.spacing(0, 2),
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    marginRight: theme.spacing(2),
    color: 'inherit'
  },
  searchInput: {
    flexGrow: 1,
    color: 'inherit',
    '& input::placeholder': {
      opacity: 1,
      color: 'inherit'
    }
  },
  searchPopper: {
    zIndex: theme.zIndex.appBar + 100
  },
  searchPopperContent: {
    marginTop: theme.spacing(1)
  },
  trialButton: {
    marginLeft: theme.spacing(2),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  btnText: {
    marginLeft: '1rem',
    color: '#fff'
  },
  trialIcon: {
    marginRight: theme.spacing(1)
  },
  notificationsButton: {
    marginLeft: theme.spacing(1)
  },
  notificationsBadge: {
    backgroundColor: colors.pink[500]
  },
  logoutButton: {
    marginLeft: theme.spacing(1)
  },
  logoutIcon: {
    marginRight: theme.spacing(1)
  },
  notificationsRoot: {
    border: '2px solid white',
    borderRadius: '50%',
    padding: 6,
    margin: -6
  },
  name: {
    color: '#fff',
    fontWeight: 200
  },
  caret: {
    '&::after': {
      content: "'▲'",
      position: 'absolute',
      bottom: '-32px',
      fontSize: '1.3rem',
      transform: 'scaleX(1.5)',
      textShadow: '0px -7px 20px rgb(0 0 0 / 20%)',
      fontFamily: 'system-ui',
      animation: '$caret 0.25s'
    }
  },
  '@keyframes caret': {
    from: {
      opacity: 0,
      bottom: '-42px'
    },
    to: {
      opacity: 1,
      bottom: '-32px'
    }
  },
  profileCaret: {
    '&::after': {
      content: "'▲'",
      position: 'absolute',
      bottom: '-23px',
      fontSize: '1.3rem',
      transform: 'scaleX(1.5)',
      textShadow: '0px -7px 20px rgb(0 0 0 / 20%)',
      fontFamily: 'system-ui',
      animation: '$profileCaret 0.25s'
    }
  },
  '@keyframes profileCaret': {
    from: {
      opacity: 0,
      bottom: '-33px'
    },
    to: {
      opacity: 1,
      bottom: '-23px'
    }
  },
  transition: {
    transition: 'all 0.3s ease-in-out'
  },
  logo: {
    width: 130,
    opacity: 1,
    [theme.breakpoints.up('md')]: {
      marginRight: 75
    }
  },
  hiddenLogo: {
    width: 0,
    marginRight: 0,
    opacity: 0
  },
  collapseNavbar: {
    backgroundColor: '#1F2125',
    padding: '9px 16px',
    borderRadius: 5,
    cursor: 'pointer'
  },
  divider: {
    height: '100%',
    width: 0,
    backgroundColor: 'white',
    marginLeft: 18.5,
    borderRight: '0.5px solid #F5F6F8'
  }
}))

const TopBar = props => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const { onOpenNavBarMobile, className, ...rest } = props
  const logo = useSelector(state => state.session?.user?.profilePhoto) || ''
  const userId = useSelector(state => state.session?.userId) || ''
  const adminFirstname =
    useSelector(state => state.session?.user?.adminFirstname) || 'Unknown'
  const isCollapsedMenu = useSelector(state => state.session?.isCollapsedMenu)
  const classes = useStyles()
  const notificationsRef = useRef(null)
  const [notifications, setNotifications] = useState([])
  const [openNotifications, setOpenNotifications] = useState(false)
  const [openProfile, setOpenProfile] = useState(false)
  const notificationsRefresher = useSelector(
    state => state.unsaved.notificationsRefresher
  )

  const token = useSelector(state => state.session?.token)
  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    var myHeaders = new Headers()
    myHeaders.append('Content-Type', 'application/json')
    myHeaders.append('Authorization', `Bearer ${token}`)

    var raw = JSON.stringify({
      userId
    })

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
      signal
    }

    fetch(`${appApiUrl}notifications/getNotifications.php`, requestOptions)
      .then(response => response.json())
      .then(result => {
        checkToken(result, dispatch)
        setNotifications(result?.notifications?.reverse().slice(0, 5))
      })
      .catch(error => {
        if (error.name === 'AbortError') return
        enqueueSnackbar(error.message, {
          variant: 'error'
        })
      })
    return () => controller.abort()
  }, [userId, token, notificationsRefresher, dispatch, enqueueSnackbar])

  const handleNotificationsOpen = () => {
    setOpenNotifications(true)
  }
  const handleProfileOpen = () => {
    setOpenProfile(true)
  }

  const handleNotificationsClose = () => {
    setOpenNotifications(false)
  }
  const handleProfileClose = () => {
    setOpenProfile(false)
  }
  const location = useLocation()
  const isNotifications = Boolean(location.pathname === '/notifications')
  const showUnreadBadge = notifications?.filter(item => item.viewed === '0')
    ?.length
  return (
    <AppBar
      style={{ transform: 'translate3d(0, 0, 200px)' }}
      {...rest}
      className={clsx(classes.root, className)}
      color="primary">
      <meta name="theme-color" content="#48494E" />
      <Toolbar>
        <Hidden lgUp>
          <IconButton color="inherit" onClick={onOpenNavBarMobile}>
            <MenuIcon />
          </IconButton>
        </Hidden>
        <RouterLink to="/dashboard">
          <img
            className={clsx(
              classes.transition,
              isCollapsedMenu ? classes.hiddenLogo : classes.logo
            )}
            alt="Logo"
            src="./images/logos/logo--white.svg"
          />
        </RouterLink>
        <Hidden mdDown>
          <img
            onClick={() => dispatch(toggleCollapseMenu())}
            className={classes.collapseNavbar}
            src={MenuCollapseIcon}
            alt="collapse menu icon"
          />
          <div className={classes.divider}></div>
        </Hidden>
        <Hidden mdDown>
          <Typography className={classes.btnText} variant="body1">
            <strong>Today </strong>
            {formatAMPM(new Date())}
          </Typography>
        </Hidden>
        <div className={classes.flexGrow} />
        <IconButton
          className={clsx(
            classes.notificationsButton,
            openNotifications ? classes.caret : null
          )}
          color="inherit"
          onClick={handleNotificationsOpen}
          ref={notificationsRef}>
          <Badge
            badgeContent={showUnreadBadge}
            classes={{
              badge: !isNotifications && classes.notificationsBadge,
              root: isNotifications && classes.notificationsRoot
            }}
            variant="dot">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <IconButton
          color="inherit"
          className={clsx(
            classes.notificationsButton,
            openProfile && classes.profileCaret
          )}
          onClick={handleProfileOpen}
          ref={notificationsRef}>
          <Avatar
            src={logo}
            style={{ margin: '0 .5em 0 0em', border: '2px solid white' }}
          />
          <Hidden smDown>
            <Typography variant="h4" className={classes.name}>
              {adminFirstname}
            </Typography>
            <ExpandMoreIcon />
          </Hidden>
        </IconButton>
      </Toolbar>
      <NotificationsPopover
        anchorEl={notificationsRef.current}
        notifications={notifications}
        onClose={handleNotificationsClose}
        open={openNotifications}
      />
      <ProfilePopover
        anchorEl={notificationsRef.current}
        onClose={handleProfileClose}
        open={openProfile}
      />
    </AppBar>
  )
}

TopBar.propTypes = {
  className: PropTypes.string,
  onOpenNavBarMobile: PropTypes.func
}

export default TopBar
